import { h, Fragment } from 'preact';

import { Translate } from '@travel/translation';
import { useDeviceTypes, useDialogHandler } from '@travel/traveler-core/hooks';
import { Divider } from '@travel/ui';
import { isNotEmptyArray } from '@travel/utils';

import CollapsibleSection from 'components/CollapsibleSection';
import RoomInfo from 'pages/PlansList/components/RoomInfo';
import PlanInfo from '../PlanInfo';

import styles from './planItem.module.scss';

import type { URLSearchQuery } from 'Common-Types';
import type { Plan } from 'PlanList-Types';

type Props = {
  /** Object of specific plan  */
  plan: Plan;
  /** String of current selected hotel id (page identifier)  */
  hotelId: string;
  /** Object of current active query */
  query?: URLSearchQuery;
  /** String of current selected departure date  */
  departureDate?: string;
  /** String of current selected return date  */
  returnDate?: string;
  /** String of accommodation area path for navigation */
  accommodationAreaPath: string;
  /** String of accommodations area paths for navigation */
  accommodationsPath: string;
  /** String of departure code for Item */
  departureAreaCode: string;
  /** Default station name used for Departure, used for Vacancy Calendar search */
  departureStation?: string;
  /** Default station name used for Arrival, used for Vacancy Calendar search */
  destinationStation?: string;
};

const PlanItem = (props: Props) => {
  const {
    plan,
    hotelId,
    departureDate,
    returnDate,
    query,
    accommodationAreaPath,
    accommodationsPath,
    departureAreaCode,
    departureStation,
    destinationStation,
  } = props;
  const { isOpen: isSeeMoreRoomOpen, onToggle: onSeeMoreRoomToggle } = useDialogHandler(false);

  const { isPC } = useDeviceTypes();

  const planItems = [...plan.items];
  const firstItemPlan = planItems.shift();

  return (
    <Fragment key={plan.planId}>
      <div className={styles.planItemContainer} data-testid="planItem-container">
        <PlanInfo
          hotelId={hotelId}
          plan={plan}
          query={query}
          accommodationAreaPath={accommodationAreaPath}
          accommodationsPath={accommodationsPath}
        />
        {firstItemPlan && (
          <RoomInfo
            item={firstItemPlan}
            isShowImage={isPC}
            planId={plan.planId}
            hotelId={hotelId}
            departureDate={departureDate}
            returnDate={returnDate}
            query={query}
            accommodationAreaPath={accommodationAreaPath}
            accommodationsPath={accommodationsPath}
            departureAreaCode={departureAreaCode}
            hasMeal={plan.hasMeal}
            departureStation={departureStation}
            destinationStation={destinationStation}
          />
        )}

        {!isSeeMoreRoomOpen && !isPC && <Divider type="solid" className={styles.divider} />}
        {isNotEmptyArray(planItems) && (
          <CollapsibleSection
            className={styles.collapsibleSection}
            textDisplayedWhileOpen={<Translate id="DP_Common.Search_Condition.Close" />}
            textDisplayedWhileClosed={
              <Translate
                id="Plan_List.List.Display_all_rooms"
                data={{ all_room_count: plan.items.length }}
              />
            }
            isOpen={isSeeMoreRoomOpen}
            onClickOpen={onSeeMoreRoomToggle}
            onClickClose={onSeeMoreRoomToggle}
          >
            {planItems.map(item => (
              <RoomInfo
                key={item.itemId}
                item={item}
                isShowImage={isPC}
                planId={plan.planId}
                hotelId={hotelId}
                query={query}
                departureDate={departureDate}
                returnDate={returnDate}
                accommodationAreaPath={accommodationAreaPath}
                accommodationsPath={accommodationsPath}
                departureAreaCode={departureAreaCode}
                hasMeal={plan.hasMeal}
                departureStation={departureStation}
                destinationStation={destinationStation}
              />
            ))}
          </CollapsibleSection>
        )}
      </div>
    </Fragment>
  );
};

export default PlanItem;
